import React, { useState, useEffect } from "react"; // Import useEffect
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
console.log("reCAPTCHA Site Key:", SITE_KEY); // Add this line to check

const LoginPage = (props) => {
  // Add useEffect for cleanup
  useEffect(() => {
    // Return a cleanup function that runs on component unmount
    return () => {
      // Find all script tags in the head
      const scripts = document.head.getElementsByTagName("script");
      for (let i = scripts.length - 1; i >= 0; i--) {
        // Check if the script src contains the reCAPTCHA API URL
        if (scripts[i].src.includes("recaptcha/api.js")) {
          // Remove the script tag
          scripts[i].parentNode.removeChild(scripts[i]);
          // console.log("Removed reCAPTCHA script tag."); // Optional: for debugging
          break; // Assuming only one script needs removal
        }
      }
      // Also remove the reCAPTCHA badge element if it exists
      const badge = document.querySelector('.grecaptcha-badge');
      if (badge) {
        badge.parentNode.removeChild(badge);
        // console.log("Removed reCAPTCHA badge."); // Optional: for debugging
      }
      // Explicitly undefine the global grecaptcha object if it exists
      if (window.grecaptcha) {
        // Add a try-catch block for safety, although direct undefining is usually fine
        try {
          // Attempt to reset if possible, otherwise just nullify
          if (typeof window.grecaptcha.reset === 'function') {
            // This might not exist or work as expected depending on the state, hence the try-catch
            window.grecaptcha.reset();
          }
        } catch (e) {
          // Ignore errors during reset attempt
        }
        window.grecaptcha = undefined;
        // console.log("Undefined window.grecaptcha."); // Optional: for debugging
      }
    };
  }, []); // Empty dependency array ensures this runs only on mount and unmount

  // Only render the provider if the site key is available
  if (!SITE_KEY) {
    // Optionally render a message or fallback UI
    return <div>Error: reCAPTCHA Site Key is not configured.</div>; 
  }

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={SITE_KEY}
      scriptProps={{
        async: true,
        defer: true,
        appendTo: "head",
        nonce: undefined,
      }}
    >
      <LoginForm {...props} />
    </GoogleReCaptchaProvider>
  );
};

const LoginForm = ({ setUserRole }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      if (!executeRecaptcha) {
        throw new Error("reCAPTCHA not ready");
      }

      const captchaToken = await executeRecaptcha("login");

      if (!captchaToken) {
        throw new Error("reCAPTCHA token missing or expired");
      }

      const response = await axios.post("https://icttestalarm.com:3000/api/login", {
        username,
        password,
        captcha: captchaToken,
      });

      if (response.data.success) {
        localStorage.setItem("userRole", response.data.role);
        setUserRole(response.data.role);
        navigate("/", { replace: true });
        window.location.reload();
      } else {
        throw new Error(response.data.message || "Login failed");
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.message ||
        "An error occurred. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <img src="/banner.jpg" alt="App Banner" style={styles.banner} />

      <div style={styles.card}>
        <h2 style={styles.title}>Welcome to STAR Emergency App</h2>

        <form onSubmit={handleLogin} style={styles.form}>
          <input
            type="text"
            placeholder="Username"
            value={username}
            style={styles.input}
            onChange={(e) => setUsername(e.target.value)}
            required
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            style={styles.input}
            onChange={(e) => setPassword(e.target.value)}
            required
          />

          <p style={styles.recaptcha}>🔐 Protected by Google reCAPTCHA</p>

          <button type="submit" style={styles.loginButton} disabled={isLoading}>
            {isLoading ? "Logging in..." : "Login"}
          </button>
        </form>

        {error && <p style={styles.error}>{error}</p>}

        <hr style={{ width: "100%", margin: "1rem 0" }} />

        <button onClick={() => navigate("/sso-login")} style={styles.googleButton}>
          Sign in with Google
        </button>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: "#f0f2f5",
    padding: "2rem",
  },
  banner: {
    width: "100%",
    maxWidth: "500px",
    marginBottom: "2rem",
    borderRadius: "10px",
    objectFit: "cover",
  },
  card: {
    width: "100%",
    maxWidth: "400px",
    background: "#fff",
    padding: "2rem",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    textAlign: "center",
  },
  title: {
    marginBottom: "1.5rem",
    color: "#333",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
  },
  input: {
    padding: "0.75rem",
    fontSize: "1rem",
    border: "1px solid #ccc",
    borderRadius: "6px",
  },
  loginButton: {
    padding: "0.75rem",
    backgroundColor: "#007aff",
    color: "#fff",
    fontWeight: "bold",
    fontSize: "1rem",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
  googleButton: {
    padding: "0.75rem",
    backgroundColor: "#DB4437",
    color: "#fff",
    fontWeight: "bold",
    fontSize: "1rem",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    width: "100%",
  },
  recaptcha: {
    fontSize: "0.85rem",
    color: "#888",
    marginTop: "-0.5rem",
  },
  error: {
    color: "red",
    marginTop: "1rem",
  },
};

export default LoginPage;
